
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "@/lib/toast";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { TrelloBoard } from "./components/TrelloBoard";
import { CreateBoardDialog } from "./components/CreateBoardDialog";
import { BoardListTabs } from "./components/BoardListTabs";
import { KanbanBoard as KanbanBoardType, Client } from "@/types/database";
import { useQuery } from "@tanstack/react-query";
import { AuthError } from "@/components/auth/AuthError";
import { useWorkflowAccess } from "@/hooks/use-workflow-access";

// Define the Board type to match what BoardListTabs expects
interface Board {
  id: string;
  name: string;
  description?: string;
  client?: { id: string; name: string } | null;
  created_at: string;
  updated_at: string;
  is_archived: boolean;
  user_id: string;
  client_id: string;
}

export default function WorkflowPage() {
  const [activeTab, setActiveTab] = useState("boards");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedBoardId, setSelectedBoardId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [boardToDelete, setBoardToDelete] = useState<KanbanBoardType | null>(null);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  
  const { accessibleBoards, isLoading: isLoadingAccessible, refreshBoards } = useWorkflowAccess();
  
  // Check authentication
  const { data: session, isLoading: isAuthLoading } = useQuery({
    queryKey: ['auth-session'],
    queryFn: async () => {
      const { data, error } = await supabase.auth.getSession();
      if (error) throw error;
      return data.session;
    }
  });
  
  // Fetch clients for board creation
  const { 
    data: clients = [],
    isLoading: isLoadingClients
  } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => {
      if (!session) return [];
      const { data, error } = await supabase
        .from('clients')
        .select('id, name')
        .eq('status', 'active')
        .eq('user_id', session.user.id)
        .order('name');
        
      if (error) throw error;
      return data as Client[];
    },
    enabled: !!session
  });

  // Transform accessible boards to match the Board interface - boards are already unique from hook
  const boards: Board[] = accessibleBoards.map(board => ({
    ...board,
    updated_at: board.updated_at || board.created_at
  }));
  
  // Apply search filter to boards
  const filteredBoards = boards.filter(board => 
    board.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    board.client?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (board.description && board.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );
  
  // Handle board selection
  const handleBoardSelect = (board: Board) => {
    console.log('Selected board:', board.id);
    setSelectedBoardId(board.id);
    setActiveTab("board");
  };
  
  // Handle board deletion
  const handleDeleteBoard = async () => {
    if (!boardToDelete) return;
    
    setDeleting(true);
    try {
      // Delete the board
      const { error } = await supabase
        .from('kanban_boards')
        .update({ is_archived: true })
        .eq('id', boardToDelete.id);
        
      if (error) throw error;
      
      toast.success("Quadro excluído com sucesso!");
      refreshBoards();
      
      // If the deleted board was the selected one, go back to boards view
      if (selectedBoardId === boardToDelete.id) {
        setSelectedBoardId(null);
        setActiveTab("boards");
      }
    } catch (error) {
      console.error("Erro ao excluir quadro:", error);
      toast.error("Erro ao excluir quadro");
    } finally {
      setDeleteDialogOpen(false);
      setBoardToDelete(null);
      setDeleting(false);
    }
  };
  
  // Handle board creation
  const handleBoardCreated = () => {
    refreshBoards();
  };
  
  if (isAuthLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[70vh]">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
        <p className="text-muted-foreground mt-4">Carregando...</p>
      </div>
    );
  }
  
  if (!session) {
    return <AuthError onTryAgain={() => navigate('/login')} />;
  }
  
  return (
    <div className="h-full w-full overflow-hidden">
      <div className="h-full flex flex-col">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4 flex-shrink-0">
            <TabsList>
              <TabsTrigger value="boards">Quadros</TabsTrigger>
              {selectedBoardId && <TabsTrigger value="board">Quadro atual</TabsTrigger>}
            </TabsList>
            
            {activeTab === "boards" ? (
              <CreateBoardDialog 
                clients={clients} 
                isLoadingClients={isLoadingClients}
                onBoardCreated={handleBoardCreated} 
              />
            ) : selectedBoardId ? (
              <Button onClick={() => setActiveTab("boards")} variant="outline">
                Voltar para Quadros
              </Button>
            ) : null}
          </div>
          
          <TabsContent value="boards" className="border-none p-0 flex-1 overflow-hidden">
            <BoardListTabs
              selectedBoard={boards.find(b => b.id === selectedBoardId) || null}
              onBoardSelect={handleBoardSelect}
              boards={filteredBoards}
              onRefresh={refreshBoards}
            />
          </TabsContent>
          
          {selectedBoardId && (
            <TabsContent value="board" className="border-none p-0 flex-1 overflow-hidden">
              <TrelloBoard boardId={selectedBoardId} />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}
